import React, { useEffect, useMemo, useState } from 'react';
import {
  User,
  Clock,
  ScrollText,
  Coins,
  Search,
  Activity,
  ChevronRight,
  ChevronLeft,
  X,
  MessageSquare,
  Bot,
  ArrowUpRight,
  ArrowDownRight,
  Monitor,
  Hash,
  Zap,
  CalendarDays,
  FilterX
} from 'lucide-react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';

export type Theme = 'dark' | 'light';

export interface ChatAgentSimulationProps {
  theme?: Theme;
  agentId?: string;
}

type LogStatus = 'success' | 'error' | 'throttled';
type Channel = 'web' | 'api' | 'slack' | 'embed';

interface ChatTurn {
  role: 'user' | 'assistant';
  text: string;
  promptTokens?: number;
  completionTokens?: number;
  latencyMs?: number;
  model?: string;
  status?: LogStatus;
  timestamp: string;
}

interface ChatSession {
  id: string;
  title: string;
  channel: Channel;
  startedAt: string;
  endedAt: string;
  status: LogStatus;
  turns: ChatTurn[];
}

interface UserUsage {
  id: string;
  name: string;
  email: string;
  initials: string;
  sessions: ChatSession[];
}

const THEME_CONFIG = {
  dark: {
    primary: 'bg-neutral-900',
    secondary: 'bg-neutral-800',
    background: 'bg-neutral-950',
    surface: 'bg-neutral-800',
    border: 'border-neutral-700',
    text: { primary: 'text-white', secondary: 'text-neutral-300', muted: 'text-neutral-400' },
    input: 'bg-neutral-700 border-neutral-600 text-white',
    hover: 'hover:bg-neutral-700',
    rowHover: 'hover:bg-neutral-800/80',
    chip: 'bg-neutral-700/80 text-neutral-300',
    divider: 'divide-neutral-700/60'
  },
  light: {
    primary: 'bg-white',
    secondary: 'bg-gray-50',
    background: 'bg-gray-100',
    surface: 'bg-white',
    border: 'border-gray-200',
    text: { primary: 'text-gray-900', secondary: 'text-gray-800', muted: 'text-gray-700' },
    input: 'bg-white border-gray-300 text-gray-900',
    hover: 'hover:bg-gray-100',
    rowHover: 'hover:bg-gray-50',
    chip: 'bg-gray-100 text-gray-700',
    divider: 'divide-gray-200'
  }
};

const PAGE_SIZE = 6;

const MOCK_USERS: UserUsage[] = [
  {
    id: 'u1',
    name: 'Maya Chen',
    email: 'maya.chen@acme.com',
    initials: 'MC',
    sessions: [
      {
        id: 's1a',
        title: 'Q3 APAC revenue summary',
        channel: 'web',
        startedAt: '2026-07-15T18:40:00Z',
        endedAt: '2026-07-15T18:44:20Z',
        status: 'success',
        turns: [
          {
            role: 'user',
            text: 'Summarize Q3 revenue vs forecast for APAC',
            timestamp: '2026-07-15T18:40:05Z'
          },
          {
            role: 'assistant',
            text: 'APAC revenue came in 6.2% above forecast, driven by enterprise renewals in Japan and Australia.',
            promptTokens: 1842,
            completionTokens: 612,
            latencyMs: 1480,
            model: 'gpt-4o',
            status: 'success',
            timestamp: '2026-07-15T18:40:12Z'
          },
          {
            role: 'user',
            text: 'Break that down by country',
            timestamp: '2026-07-15T18:42:01Z'
          },
          {
            role: 'assistant',
            text: 'Japan +8.1%, Australia +5.4%, Singapore +3.2%, rest of APAC flat vs forecast.',
            promptTokens: 920,
            completionTokens: 340,
            latencyMs: 980,
            model: 'gpt-4o',
            status: 'success',
            timestamp: '2026-07-15T18:42:08Z'
          }
        ]
      },
      {
        id: 's1b',
        title: 'Forecast variance follow-up',
        channel: 'web',
        startedAt: '2026-07-14T11:02:00Z',
        endedAt: '2026-07-14T11:05:40Z',
        status: 'success',
        turns: [
          {
            role: 'user',
            text: 'Why was Singapore below plan in June?',
            timestamp: '2026-07-14T11:02:10Z'
          },
          {
            role: 'assistant',
            text: 'Two delayed renewals totaling $410k slipped into July. Pipeline is otherwise healthy.',
            promptTokens: 640,
            completionTokens: 210,
            latencyMs: 720,
            model: 'gpt-4o-mini',
            status: 'success',
            timestamp: '2026-07-14T11:02:16Z'
          }
        ]
      }
    ]
  },
  {
    id: 'u2',
    name: 'James Ortiz',
    email: 'james.ortiz@acme.com',
    initials: 'JO',
    sessions: [
      {
        id: 's2a',
        title: 'Refund policy lookup',
        channel: 'embed',
        startedAt: '2026-07-15T18:30:00Z',
        endedAt: '2026-07-15T18:31:20Z',
        status: 'success',
        turns: [
          {
            role: 'user',
            text: 'What are the refund policies for annual plans?',
            timestamp: '2026-07-15T18:30:10Z'
          },
          {
            role: 'assistant',
            text: 'Annual plans can be refunded within 14 days of purchase if unused.',
            promptTokens: 420,
            completionTokens: 186,
            latencyMs: 640,
            model: 'gpt-4o-mini',
            status: 'success',
            timestamp: '2026-07-15T18:30:14Z'
          }
        ]
      }
    ]
  },
  {
    id: 'u3',
    name: 'Priya Nair',
    email: 'priya.nair@acme.com',
    initials: 'PN',
    sessions: [
      {
        id: 's3a',
        title: 'Ticket #4412 draft reply',
        channel: 'slack',
        startedAt: '2026-07-15T17:55:00Z',
        endedAt: '2026-07-15T18:01:10Z',
        status: 'success',
        turns: [
          {
            role: 'user',
            text: 'Draft a reply to customer ticket #4412 about delayed shipment',
            timestamp: '2026-07-15T17:55:20Z'
          },
          {
            role: 'assistant',
            text: 'I’ve prepared an empathetic reply acknowledging the delay and offering a shipping credit.',
            promptTokens: 2210,
            completionTokens: 890,
            latencyMs: 2104,
            model: 'gpt-4o',
            status: 'success',
            timestamp: '2026-07-15T17:55:28Z'
          },
          {
            role: 'user',
            text: 'Make it shorter and more formal',
            timestamp: '2026-07-15T17:58:00Z'
          },
          {
            role: 'assistant',
            text: 'Revised formal draft under 80 words, ready to paste into the ticket.',
            promptTokens: 1100,
            completionTokens: 260,
            latencyMs: 890,
            model: 'gpt-4o',
            status: 'success',
            timestamp: '2026-07-15T17:58:06Z'
          }
        ]
      },
      {
        id: 's3b',
        title: 'SLA escalation wording',
        channel: 'web',
        startedAt: '2026-07-13T09:12:00Z',
        endedAt: '2026-07-13T09:14:30Z',
        status: 'success',
        turns: [
          {
            role: 'user',
            text: 'Suggest SLA escalation language for P1 tickets',
            timestamp: '2026-07-13T09:12:10Z'
          },
          {
            role: 'assistant',
            text: 'Use clear ownership, ETA, and customer update cadence every 30 minutes.',
            promptTokens: 780,
            completionTokens: 320,
            latencyMs: 810,
            model: 'gpt-4o-mini',
            status: 'success',
            timestamp: '2026-07-13T09:12:16Z'
          }
        ]
      }
    ]
  },
  {
    id: 'u4',
    name: 'Alex Kim',
    email: 'alex.kim@acme.com',
    initials: 'AK',
    sessions: [
      {
        id: 's4a',
        title: 'Acme Corp invoices',
        channel: 'api',
        startedAt: '2026-07-15T17:20:00Z',
        endedAt: '2026-07-15T17:22:40Z',
        status: 'success',
        turns: [
          {
            role: 'user',
            text: 'List open invoices for Acme Corp over $10k',
            timestamp: '2026-07-15T17:20:12Z'
          },
          {
            role: 'assistant',
            text: 'Found 4 open invoices totaling $48,200.',
            promptTokens: 980,
            completionTokens: 340,
            latencyMs: 920,
            model: 'gpt-4o-mini',
            status: 'success',
            timestamp: '2026-07-15T17:20:18Z'
          }
        ]
      }
    ]
  },
  {
    id: 'u5',
    name: 'Sofia Reyes',
    email: 'sofia.reyes@acme.com',
    initials: 'SR',
    sessions: [
      {
        id: 's5a',
        title: 'Data retention explainer',
        channel: 'web',
        startedAt: '2026-07-15T16:44:00Z',
        endedAt: '2026-07-15T16:45:30Z',
        status: 'success',
        turns: [
          {
            role: 'user',
            text: 'Explain our data retention policy in plain language',
            timestamp: '2026-07-15T16:44:10Z'
          },
          {
            role: 'assistant',
            text: 'We keep account data for 24 months after closure, then anonymize identifiers.',
            promptTokens: 510,
            completionTokens: 278,
            latencyMs: 710,
            model: 'gpt-4o-mini',
            status: 'success',
            timestamp: '2026-07-15T16:44:14Z'
          }
        ]
      }
    ]
  },
  {
    id: 'u6',
    name: 'Dev Bot',
    email: 'devbot@acme.com',
    initials: 'DB',
    sessions: [
      {
        id: 's6a',
        title: 'Batch regression suite',
        channel: 'api',
        startedAt: '2026-07-15T16:12:00Z',
        endedAt: '2026-07-15T16:12:55Z',
        status: 'throttled',
        turns: [
          {
            role: 'user',
            text: 'POST /v1/chat — batch regression suite',
            timestamp: '2026-07-15T16:12:40Z'
          },
          {
            role: 'assistant',
            text: 'Rate limit exceeded for tenant workspace.',
            promptTokens: 0,
            completionTokens: 0,
            latencyMs: 42,
            model: 'gpt-4o',
            status: 'throttled',
            timestamp: '2026-07-15T16:12:41Z'
          }
        ]
      },
      {
        id: 's6b',
        title: 'Health check probes',
        channel: 'api',
        startedAt: '2026-07-15T08:00:00Z',
        endedAt: '2026-07-15T08:00:20Z',
        status: 'success',
        turns: [
          {
            role: 'user',
            text: 'ping',
            timestamp: '2026-07-15T08:00:05Z'
          },
          {
            role: 'assistant',
            text: 'ok',
            promptTokens: 12,
            completionTokens: 4,
            latencyMs: 88,
            model: 'gpt-4o-mini',
            status: 'success',
            timestamp: '2026-07-15T08:00:06Z'
          }
        ]
      }
    ]
  },
  {
    id: 'u7',
    name: 'Liam Brooks',
    email: 'liam.brooks@acme.com',
    initials: 'LB',
    sessions: [
      {
        id: 's7a',
        title: 'Support volume comparison',
        channel: 'web',
        startedAt: '2026-07-15T15:36:00Z',
        endedAt: '2026-07-15T15:39:10Z',
        status: 'success',
        turns: [
          {
            role: 'user',
            text: 'Compare last week’s support volume to the prior month',
            timestamp: '2026-07-15T15:36:20Z'
          },
          {
            role: 'assistant',
            text: 'Support volume rose 18% week-over-week; billing intents were the top driver.',
            promptTokens: 1560,
            completionTokens: 504,
            latencyMs: 1320,
            model: 'gpt-4o',
            status: 'success',
            timestamp: '2026-07-15T15:36:28Z'
          }
        ]
      }
    ]
  },
  {
    id: 'u8',
    name: 'Nina Patel',
    email: 'nina.patel@acme.com',
    initials: 'NP',
    sessions: [
      {
        id: 's8a',
        title: 'MFA device reset',
        channel: 'embed',
        startedAt: '2026-07-15T14:08:00Z',
        endedAt: '2026-07-15T14:09:40Z',
        status: 'error',
        turns: [
          {
            role: 'user',
            text: 'Can you reset my MFA device?',
            timestamp: '2026-07-15T14:08:20Z'
          },
          {
            role: 'assistant',
            text: 'Upstream identity provider timed out while verifying session.',
            promptTokens: 290,
            completionTokens: 48,
            latencyMs: 4800,
            model: 'gpt-4o-mini',
            status: 'error',
            timestamp: '2026-07-15T14:09:20Z'
          }
        ]
      },
      {
        id: 's8b',
        title: 'Password reset guidance',
        channel: 'web',
        startedAt: '2026-07-12T16:20:00Z',
        endedAt: '2026-07-12T16:22:00Z',
        status: 'success',
        turns: [
          {
            role: 'user',
            text: 'How do I reset my password?',
            timestamp: '2026-07-12T16:20:10Z'
          },
          {
            role: 'assistant',
            text: 'Use Account → Security → Reset password. A link is emailed for 30 minutes.',
            promptTokens: 310,
            completionTokens: 140,
            latencyMs: 520,
            model: 'gpt-4o-mini',
            status: 'success',
            timestamp: '2026-07-12T16:20:14Z'
          }
        ]
      }
    ]
  },
  {
    id: 'u9',
    name: 'Owen Park',
    email: 'owen.park@acme.com',
    initials: 'OP',
    sessions: [
      {
        id: 's9a',
        title: 'Product catalog search',
        channel: 'web',
        startedAt: '2026-07-15T13:10:00Z',
        endedAt: '2026-07-15T13:12:30Z',
        status: 'success',
        turns: [
          {
            role: 'user',
            text: 'Which SKUs include SSO?',
            timestamp: '2026-07-15T13:10:12Z'
          },
          {
            role: 'assistant',
            text: 'SSO is included on Business and Enterprise SKUs.',
            promptTokens: 450,
            completionTokens: 120,
            latencyMs: 560,
            model: 'gpt-4o-mini',
            status: 'success',
            timestamp: '2026-07-15T13:10:16Z'
          }
        ]
      }
    ]
  },
  {
    id: 'u10',
    name: 'Hannah Lee',
    email: 'hannah.lee@acme.com',
    initials: 'HL',
    sessions: [
      {
        id: 's10a',
        title: 'Onboarding checklist',
        channel: 'slack',
        startedAt: '2026-07-15T12:00:00Z',
        endedAt: '2026-07-15T12:08:00Z',
        status: 'success',
        turns: [
          {
            role: 'user',
            text: 'Generate a 5-step onboarding checklist for new CSMs',
            timestamp: '2026-07-15T12:00:20Z'
          },
          {
            role: 'assistant',
            text: '1) Access setup 2) Product tour 3) Shadow calls 4) Playbooks 5) First booked QBR.',
            promptTokens: 880,
            completionTokens: 410,
            latencyMs: 990,
            model: 'gpt-4o',
            status: 'success',
            timestamp: '2026-07-15T12:00:28Z'
          }
        ]
      }
    ]
  },
  {
    id: 'u11',
    name: 'Marcus Webb',
    email: 'marcus.webb@acme.com',
    initials: 'MW',
    sessions: [
      {
        id: 's11a',
        title: 'Churn risk accounts',
        channel: 'web',
        startedAt: '2026-07-14T19:30:00Z',
        endedAt: '2026-07-14T19:35:00Z',
        status: 'success',
        turns: [
          {
            role: 'user',
            text: 'List top 5 churn-risk accounts this quarter',
            timestamp: '2026-07-14T19:30:10Z'
          },
          {
            role: 'assistant',
            text: 'Ranked by usage drop and open severity tickets: Northwind, Contoso, Fabrikam, AdventureWorks, Globex.',
            promptTokens: 1320,
            completionTokens: 480,
            latencyMs: 1410,
            model: 'gpt-4o',
            status: 'success',
            timestamp: '2026-07-14T19:30:18Z'
          }
        ]
      }
    ]
  },
  {
    id: 'u12',
    name: 'Elena Voss',
    email: 'elena.voss@acme.com',
    initials: 'EV',
    sessions: [
      {
        id: 's12a',
        title: 'Contract renewal summary',
        channel: 'web',
        startedAt: '2026-07-14T08:15:00Z',
        endedAt: '2026-07-14T08:18:00Z',
        status: 'success',
        turns: [
          {
            role: 'user',
            text: 'Summarize renewal terms for Globex',
            timestamp: '2026-07-14T08:15:20Z'
          },
          {
            role: 'assistant',
            text: 'Renews Aug 1 · net-30 · 8% uplift option · includes priority support.',
            promptTokens: 760,
            completionTokens: 190,
            latencyMs: 680,
            model: 'gpt-4o-mini',
            status: 'success',
            timestamp: '2026-07-14T08:15:25Z'
          }
        ]
      }
    ]
  }
];

const formatTokens = (n: number) =>
  n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : String(n);

const formatCost = (tokens: number) => {
  const usd = tokens * 0.000004;
  return usd < 0.01 ? `$${usd.toFixed(4)}` : `$${usd.toFixed(3)}`;
};

const formatTime = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

const statusStyles: Record<LogStatus, string> = {
  success: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25',
  error: 'bg-rose-500/15 text-rose-400 border-rose-500/25',
  throttled: 'bg-amber-500/15 text-amber-400 border-amber-500/25'
};

const channelLabel: Record<Channel, string> = {
  web: 'Web',
  api: 'API',
  slack: 'Slack',
  embed: 'Embed'
};

function sessionTokens(session: ChatSession) {
  return session.turns.reduce(
    (sum, t) => sum + (t.promptTokens ?? 0) + (t.completionTokens ?? 0),
    0
  );
}

function userTotals(user: UserUsage, sessions?: ChatSession[]) {
  const list = sessions ?? user.sessions;
  let prompt = 0;
  let completion = 0;
  let requests = 0;
  let lastActive = '';

  for (const session of list) {
    if (!lastActive || session.endedAt > lastActive) lastActive = session.endedAt;
    for (const turn of session.turns) {
      if (turn.role === 'assistant') requests += 1;
      prompt += turn.promptTokens ?? 0;
      completion += turn.completionTokens ?? 0;
    }
  }

  return {
    sessions: list.length,
    requests,
    promptTokens: prompt,
    completionTokens: completion,
    totalTokens: prompt + completion,
    lastActive
  };
}

type DatePreset = 'today' | '7d' | '30d' | 'custom';

const REFERENCE_TODAY = '2026-07-15';

function shiftDate(isoDate: string, days: number): string {
  const d = new Date(`${isoDate}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function rangeForPreset(preset: Exclude<DatePreset, 'custom'>): { from: string; to: string } {
  if (preset === 'today') return { from: REFERENCE_TODAY, to: REFERENCE_TODAY };
  if (preset === '7d') return { from: shiftDate(REFERENCE_TODAY, -6), to: REFERENCE_TODAY };
  return { from: shiftDate(REFERENCE_TODAY, -29), to: REFERENCE_TODAY };
}

function sessionInDateRange(session: ChatSession, from: string, to: string) {
  const day = session.startedAt.slice(0, 10);
  return day >= from && day <= to;
}

function eachDayInclusive(from: string, to: string): string[] {
  const start = from <= to ? from : to;
  const end = from <= to ? to : from;
  const days: string[] = [];
  let cur = start;
  while (cur <= end) {
    days.push(cur);
    cur = shiftDate(cur, 1);
    if (days.length > 62) break;
  }
  return days;
}

const CHANNEL_COLORS: Record<Channel, string> = {
  web: '#0ea5e9',
  api: '#8b5cf6',
  slack: '#10b981',
  embed: '#f59e0b'
};

const DATE_PRESETS: { id: DatePreset; label: string }[] = [
  { id: 'today', label: 'Today' },
  { id: '7d', label: '7 days' },
  { id: '30d', label: '30 days' },
  { id: 'custom', label: 'Custom' }
];

const ChartTooltip = ({
  active,
  payload,
  label,
  theme
}: {
  active?: boolean;
  payload?: Array<{ name?: string; value?: number; color?: string }>;
  label?: string;
  theme: Theme;
}) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      className={`rounded-lg border px-3 py-2 shadow-lg text-xs ${
        theme === 'dark'
          ? 'bg-neutral-900 border-neutral-700 text-neutral-200'
          : 'bg-white border-gray-200 text-gray-800'
      }`}
    >
      {label && <p className="font-medium mb-1 opacity-80">{label}</p>}
      {payload.map((item, i) => (
        <p key={i} className="tabular-nums" style={{ color: item.color }}>
          {item.name}:{' '}
          {typeof item.value === 'number' ? item.value.toLocaleString() : item.value}
        </p>
      ))}
    </div>
  );
};

const ChatAgentSimulation: React.FC<ChatAgentSimulationProps> = ({ theme = 'dark' }) => {
  const colors = THEME_CONFIG[theme];
  const initialRange = rangeForPreset('7d');

  const [search, setSearch] = useState('');
  const [datePreset, setDatePreset] = useState<DatePreset>('7d');
  const [dateFrom, setDateFrom] = useState(initialRange.from);
  const [dateTo, setDateTo] = useState(initialRange.to);
  const [channelFilter, setChannelFilter] = useState<'all' | Channel>('all');
  const [page, setPage] = useState(1);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);

  const applyPreset = (preset: DatePreset) => {
    setDatePreset(preset);
    if (preset === 'custom') return;
    const range = rangeForPreset(preset);
    setDateFrom(range.from);
    setDateTo(range.to);
  };

  const scopedUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    const from = dateFrom <= dateTo ? dateFrom : dateTo;
    const to = dateFrom <= dateTo ? dateTo : dateFrom;

    return MOCK_USERS.map(user => {
      const sessions = user.sessions.filter(session => {
        if (!sessionInDateRange(session, from, to)) return false;
        if (channelFilter !== 'all' && session.channel !== channelFilter) return false;
        return true;
      });
      return { ...user, sessions };
    })
      .filter(user => user.sessions.length > 0)
      .filter(user => {
        if (!q) return true;
        return (
          user.name.toLowerCase().includes(q) ||
          user.email.toLowerCase().includes(q)
        );
      })
      .sort((a, b) => userTotals(b).totalTokens - userTotals(a).totalTokens);
  }, [search, dateFrom, dateTo, channelFilter]);

  const totalPages = Math.max(1, Math.ceil(scopedUsers.length / PAGE_SIZE));

  useEffect(() => {
    setPage(1);
  }, [search, dateFrom, dateTo, channelFilter]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pageUsers = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return scopedUsers.slice(start, start + PAGE_SIZE);
  }, [scopedUsers, page]);

  const overview = useMemo(() => {
    const from = dateFrom <= dateTo ? dateFrom : dateTo;
    const to = dateFrom <= dateTo ? dateTo : dateFrom;
    const totals = scopedUsers.map(u => ({ user: u, ...userTotals(u) }));
    const tokens = totals.reduce((s, t) => s + t.totalTokens, 0);
    const sessions = totals.reduce((s, t) => s + t.sessions, 0);
    const requests = totals.reduce((s, t) => s + t.requests, 0);
    let success = 0;
    let error = 0;
    let throttled = 0;
    const channelCounts: Record<Channel, number> = { web: 0, api: 0, slack: 0, embed: 0 };
    const dailyMap = new Map<string, { tokens: number; sessions: number; requests: number }>();

    for (const day of eachDayInclusive(from, to)) {
      dailyMap.set(day, { tokens: 0, sessions: 0, requests: 0 });
    }

    for (const user of scopedUsers) {
      for (const session of user.sessions) {
        channelCounts[session.channel] += 1;
        if (session.status === 'success') success += 1;
        else if (session.status === 'error') error += 1;
        else throttled += 1;

        const day = session.startedAt.slice(0, 10);
        const bucket = dailyMap.get(day) ?? { tokens: 0, sessions: 0, requests: 0 };
        bucket.sessions += 1;
        bucket.tokens += sessionTokens(session);
        bucket.requests += session.turns.filter(t => t.role === 'assistant').length;
        dailyMap.set(day, bucket);
      }
    }

    const dailySeries = Array.from(dailyMap.entries()).map(([day, vals]) => ({
      day,
      label: new Date(`${day}T12:00:00Z`).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric'
      }),
      tokens: vals.tokens,
      sessions: vals.sessions,
      requests: vals.requests
    }));

    return {
      users: scopedUsers.length,
      sessions,
      tokens,
      requests,
      cost: formatCost(tokens),
      successRate: sessions ? Math.round((success / sessions) * 100) : 0,
      status: { success, error, throttled },
      topUserBars: [...totals].slice(0, 5).map(row => ({
        id: row.user.id,
        name: row.user.name.split(' ')[0],
        fullName: row.user.name,
        tokens: row.totalTokens,
        sessions: row.sessions
      })),
      channelRows: (Object.keys(channelCounts) as Channel[])
        .map(channel => ({
          channel,
          name: channelLabel[channel],
          count: channelCounts[channel],
          pct: sessions ? Math.round((channelCounts[channel] / sessions) * 100) : 0,
          fill: CHANNEL_COLORS[channel]
        }))
        .filter(r => r.count > 0)
        .sort((a, b) => b.count - a.count),
      dailySeries,
      statusPie: [
        { name: 'Success', value: success, fill: '#34d399' },
        { name: 'Error', value: error, fill: '#fb7185' },
        { name: 'Throttled', value: throttled, fill: '#fbbf24' }
      ].filter(d => d.value > 0)
    };
  }, [scopedUsers, dateFrom, dateTo]);

  const hasActiveFilters =
    search.trim() !== '' ||
    channelFilter !== 'all' ||
    datePreset !== '7d' ||
    dateFrom !== initialRange.from ||
    dateTo !== initialRange.to;

  const clearFilters = () => {
    setSearch('');
    setChannelFilter('all');
    applyPreset('7d');
  };

  const selectedUser = selectedUserId
    ? scopedUsers.find(u => u.id === selectedUserId) ??
      MOCK_USERS.find(u => u.id === selectedUserId) ??
      null
    : null;

  const openUser = (userId: string) => {
    setSelectedUserId(userId);
    const user =
      scopedUsers.find(u => u.id === userId) ?? MOCK_USERS.find(u => u.id === userId);
    setSelectedSessionId(user?.sessions[0]?.id ?? null);
  };

  const closeModal = () => {
    setSelectedUserId(null);
    setSelectedSessionId(null);
  };

  const selectedSession =
    selectedUser && selectedSessionId
      ? selectedUser.sessions.find(s => s.id === selectedSessionId) ?? null
      : null;

  const selectedSessionMetrics = useMemo(() => {
    if (!selectedSession) return null;
    const assistantTurns = selectedSession.turns.filter(t => t.role === 'assistant');
    const prompt = assistantTurns.reduce((s, t) => s + (t.promptTokens ?? 0), 0);
    const completion = assistantTurns.reduce((s, t) => s + (t.completionTokens ?? 0), 0);
    const avgLatency = assistantTurns.length
      ? Math.round(
          assistantTurns.reduce((s, t) => s + (t.latencyMs ?? 0), 0) / assistantTurns.length
        )
      : 0;
    return {
      messages: selectedSession.turns.length,
      prompt,
      completion,
      total: prompt + completion,
      avgLatency,
      models: Array.from(
        new Set(
          assistantTurns
            .map(t => t.model)
            .filter((m): m is string => Boolean(m))
        )
      )
    };
  }, [selectedSession]);

  const axisTick = theme === 'dark' ? '#a3a3a3' : '#737373';
  const gridStroke = theme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';

  return (
    <div className={`h-full ${colors.background} flex flex-col overflow-hidden`}>
      <div className={`flex-shrink-0 px-6 py-4 border-b ${colors.border} ${colors.primary}`}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className={`text-lg font-semibold ${colors.text.primary} flex items-center gap-2`}>
              <ScrollText className="w-5 h-5 text-indigo-400" />
              Usage logs
            </h1>
            <p className={`text-sm ${colors.text.muted} mt-0.5`}>
              Monitor users, tokens, and chat sessions for this agent
            </p>
          </div>
          <div className={`hidden sm:flex items-center gap-2 text-xs ${colors.text.muted}`}>
            <CalendarDays className="w-3.5 h-3.5" />
            {dateFrom === dateTo ? dateFrom : `${dateFrom} → ${dateTo}`}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="p-5 lg:p-6 max-w-6xl mx-auto space-y-5">
          {/* Filters */}
          <div className={`${colors.surface} border ${colors.border} rounded-xl p-4 space-y-3`}>
            <div className="flex flex-col xl:flex-row gap-3 xl:items-center">
              <div className="relative flex-1 min-w-0">
                <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${colors.text.muted}`} />
                <input
                  type="search"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search by name or email…"
                  className={`w-full pl-9 pr-3 py-2 rounded-lg border text-sm ${colors.input} outline-none focus:ring-2 focus:ring-indigo-500/40`}
                />
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {DATE_PRESETS.map(preset => (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => applyPreset(preset.id)}
                    className={`px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                      datePreset === preset.id
                        ? 'border-indigo-500/50 bg-indigo-500/15 text-indigo-300'
                        : `${colors.border} ${colors.chip} ${colors.hover}`
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col lg:flex-row gap-3 lg:items-end">
              <div className="flex flex-col sm:flex-row gap-3 flex-1">
                <div className="flex-1 min-w-0">
                  <label className={`block text-[11px] font-medium mb-1 ${colors.text.muted}`} htmlFor="usage-date-from">
                    From
                  </label>
                  <input
                    id="usage-date-from"
                    type="date"
                    value={dateFrom}
                    onChange={e => {
                      setDatePreset('custom');
                      setDateFrom(e.target.value);
                    }}
                    className={`w-full px-3 py-2 rounded-lg border text-sm ${colors.input} outline-none focus:ring-2 focus:ring-indigo-500/40`}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <label className={`block text-[11px] font-medium mb-1 ${colors.text.muted}`} htmlFor="usage-date-to">
                    To
                  </label>
                  <input
                    id="usage-date-to"
                    type="date"
                    value={dateTo}
                    onChange={e => {
                      setDatePreset('custom');
                      setDateTo(e.target.value);
                    }}
                    className={`w-full px-3 py-2 rounded-lg border text-sm ${colors.input} outline-none focus:ring-2 focus:ring-indigo-500/40`}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <label className={`block text-[11px] font-medium mb-1 ${colors.text.muted}`} htmlFor="usage-channel">
                    Channel
                  </label>
                  <select
                    id="usage-channel"
                    value={channelFilter}
                    onChange={e => setChannelFilter(e.target.value as 'all' | Channel)}
                    className={`w-full px-3 py-2 rounded-lg border text-sm ${colors.input} outline-none focus:ring-2 focus:ring-indigo-500/40`}
                  >
                    <option value="all">All channels</option>
                    <option value="web">Web</option>
                    <option value="api">API</option>
                    <option value="slack">Slack</option>
                    <option value="embed">Embed</option>
                  </select>
                </div>
              </div>

              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-400 hover:text-indigo-300 whitespace-nowrap"
                >
                  <FilterX className="w-3.5 h-3.5" />
                  Reset filters
                </button>
              )}
            </div>
          </div>

          {/* KPI strip */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            {[
              { label: 'Users', value: String(overview.users), icon: User, tone: 'text-sky-400' },
              { label: 'Sessions', value: String(overview.sessions), icon: MessageSquare, tone: 'text-indigo-400' },
              { label: 'Requests', value: String(overview.requests), icon: Activity, tone: 'text-amber-400' },
              { label: 'Tokens', value: formatTokens(overview.tokens), icon: Coins, tone: 'text-violet-400' },
              { label: 'Est. cost', value: overview.cost, icon: Zap, tone: 'text-emerald-400' }
            ].map(kpi => (
              <div key={kpi.label} className={`${colors.surface} border ${colors.border} rounded-xl px-4 py-3`}>
                <div className="flex items-center gap-1.5 mb-1">
                  <kpi.icon className={`w-3.5 h-3.5 ${kpi.tone}`} />
                  <span className={`text-[11px] font-medium ${colors.text.muted}`}>{kpi.label}</span>
                </div>
                <p className={`text-xl font-semibold tabular-nums ${colors.text.primary}`}>{kpi.value}</p>
              </div>
            ))}
          </div>

          {/* Charts */}
          <div className="grid lg:grid-cols-3 gap-3">
            <div className={`lg:col-span-2 ${colors.surface} border ${colors.border} rounded-xl p-4`}>
              <div className="flex items-center justify-between mb-1">
                <div>
                  <p className={`text-sm font-semibold ${colors.text.primary}`}>Token usage trend</p>
                  <p className={`text-[11px] ${colors.text.muted}`}>Daily tokens in selected range</p>
                </div>
                <span className={`text-[11px] tabular-nums ${colors.text.muted}`}>
                  {formatTokens(overview.tokens)} total
                </span>
              </div>
              <div className="h-56 mt-2">
                {overview.dailySeries.every(d => d.tokens === 0) ? (
                  <div className={`h-full flex items-center justify-center text-sm ${colors.text.muted}`}>
                    No token activity in this range
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={overview.dailySeries} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="usageTokensFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#818cf8" stopOpacity={0.45} />
                          <stop offset="100%" stopColor="#818cf8" stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
                      <XAxis
                        dataKey="label"
                        tick={{ fill: axisTick, fontSize: 11 }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        tick={{ fill: axisTick, fontSize: 11 }}
                        axisLine={false}
                        tickLine={false}
                        width={40}
                        tickFormatter={v => formatTokens(Number(v))}
                      />
                      <Tooltip content={<ChartTooltip theme={theme} />} />
                      <Area
                        type="monotone"
                        dataKey="tokens"
                        name="Tokens"
                        stroke="#818cf8"
                        strokeWidth={2.5}
                        fill="url(#usageTokensFill)"
                        activeDot={{ r: 4, fill: '#6366f1' }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            <div className={`${colors.surface} border ${colors.border} rounded-xl p-4`}>
              <div className="mb-1">
                <p className={`text-sm font-semibold ${colors.text.primary}`}>Channels</p>
                <p className={`text-[11px] ${colors.text.muted}`}>Sessions by channel</p>
              </div>
              <div className="h-56 relative">
                {overview.channelRows.length === 0 ? (
                  <div className={`h-full flex items-center justify-center text-sm ${colors.text.muted}`}>
                    No sessions
                  </div>
                ) : (
                  <>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={overview.channelRows}
                          dataKey="count"
                          nameKey="name"
                          cx="50%"
                          cy="48%"
                          innerRadius={52}
                          outerRadius={78}
                          paddingAngle={3}
                          stroke="none"
                        >
                          {overview.channelRows.map(row => (
                            <Cell key={row.channel} fill={row.fill} />
                          ))}
                        </Pie>
                        <Tooltip content={<ChartTooltip theme={theme} />} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="absolute inset-x-0 bottom-0 flex flex-wrap justify-center gap-x-3 gap-y-1 px-2">
                      {overview.channelRows.map(row => (
                        <span key={row.channel} className={`inline-flex items-center gap-1.5 text-[10px] ${colors.text.muted}`}>
                          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: row.fill }} />
                          {row.name} {row.pct}%
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-5 gap-3">
            <div className={`lg:col-span-3 ${colors.surface} border ${colors.border} rounded-xl p-4`}>
              <div className="flex items-center justify-between mb-1">
                <div>
                  <p className={`text-sm font-semibold ${colors.text.primary}`}>Top consumers</p>
                  <p className={`text-[11px] ${colors.text.muted}`}>Highest token usage</p>
                </div>
                <span className={`text-[11px] ${colors.text.muted}`}>{overview.successRate}% success</span>
              </div>
              <div className="h-56 mt-2">
                {overview.topUserBars.length === 0 ? (
                  <div className={`h-full flex items-center justify-center text-sm ${colors.text.muted}`}>
                    No usage in this date range
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={overview.topUserBars}
                      layout="vertical"
                      margin={{ top: 4, right: 12, left: 4, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} horizontal={false} />
                      <XAxis
                        type="number"
                        tick={{ fill: axisTick, fontSize: 11 }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={v => formatTokens(Number(v))}
                      />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={56}
                        tick={{ fill: axisTick, fontSize: 11 }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip
                        content={<ChartTooltip theme={theme} />}
                        cursor={{ fill: theme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)' }}
                      />
                      <Bar
                        dataKey="tokens"
                        name="Tokens"
                        radius={[0, 6, 6, 0]}
                        fill="#6366f1"
                        cursor="pointer"
                        onClick={(data: { id?: string }) => {
                          if (data?.id) openUser(data.id);
                        }}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            <div className={`lg:col-span-2 ${colors.surface} border ${colors.border} rounded-xl p-4`}>
              <div className="mb-1">
                <p className={`text-sm font-semibold ${colors.text.primary}`}>Session health</p>
                <p className={`text-[11px] ${colors.text.muted}`}>Success vs failures</p>
              </div>
              <div className="h-56 relative">
                {overview.statusPie.length === 0 ? (
                  <div className={`h-full flex items-center justify-center text-sm ${colors.text.muted}`}>
                    No sessions
                  </div>
                ) : (
                  <>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={overview.statusPie}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="46%"
                          innerRadius={48}
                          outerRadius={74}
                          paddingAngle={4}
                          stroke="none"
                        >
                          {overview.statusPie.map(row => (
                            <Cell key={row.name} fill={row.fill} />
                          ))}
                        </Pie>
                        <Tooltip content={<ChartTooltip theme={theme} />} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="absolute inset-x-0 bottom-1 flex flex-wrap justify-center gap-x-3 gap-y-1 px-2">
                      {overview.statusPie.map(row => (
                        <span key={row.name} className={`inline-flex items-center gap-1.5 text-[10px] ${colors.text.muted}`}>
                          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: row.fill }} />
                          {row.name} {row.value}
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Users table */}
          <div className={`${colors.surface} border ${colors.border} rounded-xl overflow-hidden`}>
            <div className={`px-4 py-3 border-b ${colors.border} ${colors.secondary} flex items-center justify-between gap-3`}>
              <div>
                <p className={`text-sm font-semibold ${colors.text.primary}`}>Users</p>
                <p className={`text-[11px] ${colors.text.muted}`}>
                  Click a user to open the chat monitor
                </p>
              </div>
              <span className={`text-xs tabular-nums ${colors.text.muted}`}>
                {scopedUsers.length} result{scopedUsers.length === 1 ? '' : 's'}
              </span>
            </div>

            <div
              className={`hidden md:grid grid-cols-[minmax(0,1.5fr)_80px_88px_120px_88px_130px] gap-3 px-4 py-2.5 border-b ${colors.border} text-[11px] font-medium uppercase tracking-wide ${colors.text.muted}`}
            >
              <span>User</span>
              <span>Sessions</span>
              <span>Requests</span>
              <span>Tokens</span>
              <span>Cost</span>
              <span>Last active</span>
            </div>

            <div className={`divide-y ${colors.divider}`}>
              {pageUsers.length === 0 && (
                <div className={`px-4 py-12 text-center`}>
                  <p className={`text-sm ${colors.text.primary}`}>No users found</p>
                  <p className={`text-xs ${colors.text.muted} mt-1`}>
                    Try widening the date range or clearing filters.
                  </p>
                </div>
              )}
              {pageUsers.map(user => {
                const totals = userTotals(user);
                return (
                  <button
                    key={user.id}
                    type="button"
                    onClick={() => openUser(user.id)}
                    className={`w-full text-left px-4 py-3.5 transition-colors ${colors.rowHover}`}
                  >
                    <div className="md:grid md:grid-cols-[minmax(0,1.5fr)_80px_88px_120px_88px_130px] md:gap-3 md:items-center space-y-2 md:space-y-0">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-full bg-indigo-600 text-white text-xs font-semibold flex items-center justify-center flex-shrink-0">
                          {user.initials}
                        </div>
                        <div className="min-w-0">
                          <p className={`text-sm font-medium truncate ${colors.text.primary}`}>{user.name}</p>
                          <p className={`text-[11px] truncate ${colors.text.muted}`}>{user.email}</p>
                        </div>
                      </div>
                      <p className={`text-sm tabular-nums ${colors.text.secondary}`}>{totals.sessions}</p>
                      <p className={`text-sm tabular-nums ${colors.text.secondary}`}>{totals.requests}</p>
                      <div>
                        <p className={`text-sm font-semibold tabular-nums ${colors.text.primary}`}>
                          {formatTokens(totals.totalTokens)}
                        </p>
                        <p className={`text-[11px] tabular-nums ${colors.text.muted}`}>
                          {totals.promptTokens.toLocaleString()} / {totals.completionTokens.toLocaleString()}
                        </p>
                      </div>
                      <p className={`text-sm tabular-nums ${colors.text.secondary}`}>
                        {formatCost(totals.totalTokens)}
                      </p>
                      <div className="flex items-center justify-between gap-2">
                        <p className={`text-xs ${colors.text.muted}`}>
                          {totals.lastActive ? formatTime(totals.lastActive) : '—'}
                        </p>
                        <ChevronRight className={`w-4 h-4 ${colors.text.muted}`} />
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className={`flex items-center justify-between gap-3 px-4 py-3 border-t ${colors.border} ${colors.secondary}`}>
              <p className={`text-xs ${colors.text.muted}`}>
                {scopedUsers.length === 0
                  ? '0 users'
                  : `Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, scopedUsers.length)} of ${scopedUsers.length}`}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium border ${colors.border} ${colors.chip} disabled:opacity-40 disabled:cursor-not-allowed ${colors.hover}`}
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                  Prev
                </button>
                <span className={`text-xs tabular-nums ${colors.text.muted}`}>
                  {page} / {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium border ${colors.border} ${colors.chip} disabled:opacity-40 disabled:cursor-not-allowed ${colors.hover}`}
                >
                  Next
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {selectedUser && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-5 bg-black/70 backdrop-blur-md"
          onClick={closeModal}
          role="presentation"
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="usage-user-title"
            onClick={e => e.stopPropagation()}
            className={`w-full max-w-[1280px] h-[92vh] flex flex-col rounded-2xl border ${colors.border} ${colors.primary} shadow-2xl overflow-hidden`}
          >
            {/* Monitor header */}
            <div className={`flex-shrink-0 flex items-center justify-between gap-4 px-5 py-3.5 border-b ${colors.border}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-indigo-600/20 border border-indigo-500/30 flex items-center justify-center flex-shrink-0">
                  <Monitor className="w-5 h-5 text-indigo-400" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 id="usage-user-title" className={`text-base font-semibold ${colors.text.primary}`}>
                      Chat monitor
                    </h2>
                    <span className={`text-[11px] px-2 py-0.5 rounded-full ${colors.chip}`}>
                      Live replay
                    </span>
                  </div>
                  <p className={`text-xs ${colors.text.muted} truncate`}>
                    Review sessions, full conversation, and token spend for this user
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 flex-shrink-0">
                <div className={`hidden sm:flex items-center gap-2.5 px-3 py-1.5 rounded-xl border ${colors.border} ${colors.secondary}`}>
                  <div className="w-8 h-8 rounded-full bg-indigo-600 text-white text-xs font-semibold flex items-center justify-center">
                    {selectedUser.initials}
                  </div>
                  <div className="min-w-0">
                    <p className={`text-sm font-medium ${colors.text.primary}`}>{selectedUser.name}</p>
                    <p className={`text-[11px] ${colors.text.muted}`}>{selectedUser.email}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={closeModal}
                  className={`p-2 rounded-lg border ${colors.border} ${colors.hover} ${colors.text.muted}`}
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* User totals strip */}
            {(() => {
              const totals = userTotals(selectedUser);
              return (
                <div
                  className={`flex-shrink-0 grid grid-cols-2 sm:grid-cols-5 gap-px border-b ${colors.border} ${theme === 'dark' ? 'bg-neutral-700' : 'bg-gray-200'}`}
                >
                  {[
                    { label: 'Sessions', value: String(totals.sessions), icon: MessageSquare },
                    { label: 'Requests', value: String(totals.requests), icon: Activity },
                    { label: 'Prompt tokens', value: totals.promptTokens.toLocaleString(), icon: ArrowUpRight },
                    { label: 'Completion', value: totals.completionTokens.toLocaleString(), icon: ArrowDownRight },
                    { label: 'Est. cost', value: formatCost(totals.totalTokens), icon: Coins }
                  ].map(item => (
                    <div key={item.label} className={`px-4 py-2.5 ${colors.secondary}`}>
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <item.icon className={`w-3 h-3 ${colors.text.muted}`} />
                        <span className={`text-[10px] uppercase tracking-wide ${colors.text.muted}`}>
                          {item.label}
                        </span>
                      </div>
                      <p className={`text-sm font-semibold tabular-nums ${colors.text.primary}`}>{item.value}</p>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* 3-pane body */}
            <div className="flex-1 min-h-0 flex flex-col lg:flex-row">
              {/* Sessions rail */}
              <aside
                className={`flex-shrink-0 lg:w-[280px] border-b lg:border-b-0 lg:border-r ${colors.border} ${colors.secondary} flex flex-col max-h-[34%] lg:max-h-none`}
              >
                <div className={`px-4 py-3 border-b ${colors.border}`}>
                  <p className={`text-xs font-semibold uppercase tracking-wide ${colors.text.muted}`}>
                    Chat sessions
                  </p>
                  <p className={`text-[11px] ${colors.text.muted} mt-0.5`}>
                    {selectedUser.sessions.length} recorded
                  </p>
                </div>
                <div className="flex-1 overflow-y-auto p-2 space-y-1">
                  {selectedUser.sessions.map(session => {
                    const active = selectedSessionId === session.id;
                    const tokens = sessionTokens(session);
                    return (
                      <button
                        key={session.id}
                        type="button"
                        onClick={() => setSelectedSessionId(session.id)}
                        className={`w-full text-left rounded-xl px-3 py-2.5 transition-colors border ${
                          active
                            ? 'border-indigo-500/50 bg-indigo-500/15'
                            : `${colors.border} border-transparent ${colors.hover}`
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <p
                            className={`text-sm font-medium line-clamp-2 ${
                              active ? 'text-indigo-200' : colors.text.primary
                            }`}
                          >
                            {session.title}
                          </p>
                          <span
                            className={`flex-shrink-0 mt-0.5 w-1.5 h-1.5 rounded-full ${
                              session.status === 'success'
                                ? 'bg-emerald-400'
                                : session.status === 'error'
                                ? 'bg-rose-400'
                                : 'bg-amber-400'
                            }`}
                          />
                        </div>
                        <div className={`mt-1.5 flex items-center justify-between gap-2 text-[11px] ${colors.text.muted}`}>
                          <span>
                            {channelLabel[session.channel]} ·{' '}
                            {session.turns.filter(t => t.role === 'user').length} msgs
                          </span>
                          <span className="tabular-nums font-medium">{formatTokens(tokens)}</span>
                        </div>
                        <p className={`text-[10px] mt-1 ${colors.text.muted}`}>
                          {formatTime(session.startedAt)}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </aside>

              {/* Chat transcript */}
              <section className={`flex-1 min-w-0 min-h-0 flex flex-col ${colors.background}`}>
                <div className={`flex-shrink-0 px-5 py-3 border-b ${colors.border} ${colors.primary}`}>
                  {selectedSession ? (
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className={`text-sm font-semibold truncate ${colors.text.primary}`}>
                          {selectedSession.title}
                        </p>
                        <p className={`text-[11px] ${colors.text.muted}`}>
                          {channelLabel[selectedSession.channel]} · started{' '}
                          {formatTime(selectedSession.startedAt)} · ended{' '}
                          {formatTime(selectedSession.endedAt)}
                        </p>
                      </div>
                      <span
                        className={`flex-shrink-0 px-2 py-0.5 rounded-md text-[11px] font-medium border capitalize ${statusStyles[selectedSession.status]}`}
                      >
                        {selectedSession.status}
                      </span>
                    </div>
                  ) : (
                    <p className={`text-sm ${colors.text.muted}`}>Select a session</p>
                  )}
                </div>

                <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5">
                  {!selectedSession && (
                    <div className={`h-full flex items-center justify-center text-sm ${colors.text.muted}`}>
                      Choose a chat session to inspect the full conversation.
                    </div>
                  )}
                  {selectedSession && (
                    <div className="max-w-3xl mx-auto space-y-4">
                      {selectedSession.turns.map((turn, idx) => {
                        const isUser = turn.role === 'user';
                        return (
                          <div
                            key={`${selectedSession.id}-${idx}`}
                            className={`flex gap-3 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}
                          >
                            <div
                              className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                isUser ? 'bg-neutral-600' : 'bg-indigo-600'
                              }`}
                            >
                              {isUser ? (
                                <User className="w-4 h-4 text-neutral-200" />
                              ) : (
                                <Bot className="w-4 h-4 text-white" />
                              )}
                            </div>
                            <div className={`max-w-[85%] ${isUser ? 'items-end' : 'items-start'} flex flex-col`}>
                              <div className={`flex items-center gap-2 mb-1 ${isUser ? 'flex-row-reverse' : ''}`}>
                                <span className={`text-[11px] font-medium capitalize ${colors.text.secondary}`}>
                                  {isUser ? selectedUser.name.split(' ')[0] : 'Agent'}
                                </span>
                                <span className={`text-[10px] ${colors.text.muted}`}>
                                  {formatTime(turn.timestamp)}
                                </span>
                              </div>
                              <div
                                className={`rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                                  isUser
                                    ? 'bg-indigo-600 text-white rounded-tr-md'
                                    : `${colors.surface} border ${colors.border} ${colors.text.primary} rounded-tl-md`
                                }`}
                              >
                                {turn.text}
                              </div>
                              {!isUser && (
                                <div className={`mt-1.5 flex flex-wrap gap-2 text-[10px] ${colors.text.muted}`}>
                                  {turn.model && (
                                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${colors.chip}`}>
                                      <Zap className="w-2.5 h-2.5" />
                                      {turn.model}
                                    </span>
                                  )}
                                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${colors.chip}`}>
                                    <ArrowUpRight className="w-2.5 h-2.5 text-sky-400" />
                                    {(turn.promptTokens ?? 0).toLocaleString()}
                                  </span>
                                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${colors.chip}`}>
                                    <ArrowDownRight className="w-2.5 h-2.5 text-violet-400" />
                                    {(turn.completionTokens ?? 0).toLocaleString()}
                                  </span>
                                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${colors.chip}`}>
                                    <Clock className="w-2.5 h-2.5 text-amber-400" />
                                    {turn.latencyMs ?? 0}ms
                                  </span>
                                  {turn.status && turn.status !== 'success' && (
                                    <span
                                      className={`px-1.5 py-0.5 rounded border capitalize ${statusStyles[turn.status]}`}
                                    >
                                      {turn.status}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </section>

              {/* Session insights */}
              <aside
                className={`flex-shrink-0 lg:w-[260px] border-t lg:border-t-0 lg:border-l ${colors.border} ${colors.secondary} flex flex-col max-h-[28%] lg:max-h-none`}
              >
                <div className={`px-4 py-3 border-b ${colors.border}`}>
                  <p className={`text-xs font-semibold uppercase tracking-wide ${colors.text.muted}`}>
                    Session insights
                  </p>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {!selectedSessionMetrics || !selectedSession ? (
                    <p className={`text-xs ${colors.text.muted}`}>Select a session to see metrics.</p>
                  ) : (
                    <>
                      <div className={`rounded-xl border ${colors.border} ${colors.surface} p-3 space-y-3`}>
                        <div className="flex items-center justify-between">
                          <span className={`text-[11px] ${colors.text.muted}`}>Messages</span>
                          <span className={`text-sm font-semibold tabular-nums ${colors.text.primary}`}>
                            {selectedSessionMetrics.messages}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className={`text-[11px] ${colors.text.muted}`}>Avg latency</span>
                          <span className={`text-sm font-semibold tabular-nums ${colors.text.primary}`}>
                            {selectedSessionMetrics.avgLatency}ms
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className={`text-[11px] ${colors.text.muted}`}>Est. cost</span>
                          <span className={`text-sm font-semibold tabular-nums ${colors.text.primary}`}>
                            {formatCost(selectedSessionMetrics.total)}
                          </span>
                        </div>
                      </div>

                      <div>
                        <p className={`text-[11px] font-medium ${colors.text.muted} mb-2`}>Token split</p>
                        <div className="space-y-2">
                          <div>
                            <div className="flex justify-between text-[11px] mb-1">
                              <span className={colors.text.muted}>Prompt</span>
                              <span className={`tabular-nums ${colors.text.secondary}`}>
                                {selectedSessionMetrics.prompt.toLocaleString()}
                              </span>
                            </div>
                            <div className={`h-1.5 rounded-full overflow-hidden ${theme === 'dark' ? 'bg-neutral-700' : 'bg-gray-200'}`}>
                              <div
                                className="h-full rounded-full bg-sky-500"
                                style={{
                                  width: `${
                                    selectedSessionMetrics.total
                                      ? (selectedSessionMetrics.prompt / selectedSessionMetrics.total) * 100
                                      : 0
                                  }%`
                                }}
                              />
                            </div>
                          </div>
                          <div>
                            <div className="flex justify-between text-[11px] mb-1">
                              <span className={colors.text.muted}>Completion</span>
                              <span className={`tabular-nums ${colors.text.secondary}`}>
                                {selectedSessionMetrics.completion.toLocaleString()}
                              </span>
                            </div>
                            <div className={`h-1.5 rounded-full overflow-hidden ${theme === 'dark' ? 'bg-neutral-700' : 'bg-gray-200'}`}>
                              <div
                                className="h-full rounded-full bg-violet-500"
                                style={{
                                  width: `${
                                    selectedSessionMetrics.total
                                      ? (selectedSessionMetrics.completion / selectedSessionMetrics.total) * 100
                                      : 0
                                  }%`
                                }}
                              />
                            </div>
                          </div>
                          <div className="flex items-center justify-between pt-1">
                            <span className={`text-[11px] ${colors.text.muted}`}>Total</span>
                            <span className={`text-sm font-semibold tabular-nums ${colors.text.primary}`}>
                              {selectedSessionMetrics.total.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div>
                        <p className={`text-[11px] font-medium ${colors.text.muted} mb-2`}>Models</p>
                        <div className="flex flex-wrap gap-1.5">
                          {selectedSessionMetrics.models.length === 0 ? (
                            <span className={`text-[11px] ${colors.text.muted}`}>—</span>
                          ) : (
                            selectedSessionMetrics.models.map(model => (
                              <span
                                key={model}
                                className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md ${colors.chip}`}
                              >
                                <Hash className="w-3 h-3" />
                                {model}
                              </span>
                            ))
                          )}
                        </div>
                      </div>

                      <div>
                        <p className={`text-[11px] font-medium ${colors.text.muted} mb-2`}>Channel</p>
                        <span className={`inline-flex text-xs px-2 py-1 rounded-md ${colors.chip}`}>
                          {channelLabel[selectedSession.channel]}
                        </span>
                      </div>

                      <div className={`rounded-xl border ${colors.border} ${colors.surface} p-3`}>
                        <p className={`text-[11px] font-medium ${colors.text.muted} mb-2`}>Turn timeline</p>
                        <div className="space-y-2">
                          {selectedSession.turns.map((turn, idx) => (
                            <div key={`tl-${idx}`} className="flex items-center gap-2">
                              <div
                                className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                                  turn.role === 'user' ? 'bg-indigo-400' : 'bg-emerald-400'
                                }`}
                              />
                              <span className={`text-[10px] truncate flex-1 ${colors.text.secondary}`}>
                                {turn.role === 'user' ? 'User' : 'Agent'} ·{' '}
                                {turn.text.slice(0, 36)}
                                {turn.text.length > 36 ? '…' : ''}
                              </span>
                              {turn.role === 'assistant' && (
                                <span className={`text-[10px] tabular-nums ${colors.text.muted}`}>
                                  {((turn.promptTokens ?? 0) + (turn.completionTokens ?? 0)).toLocaleString()}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </aside>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatAgentSimulation;
